import { 
  LayoutDashboard, Package, ShoppingCart, Users, 
  UserPlus, UserCog, PackageX, MessageCircle, 
  Wallet, BarChart3, Tag, Ticket, Truck, Store, Settings, PackageCheck, Layers, FileText, Mail, Wand2
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from '@/components/ui/sidebar';
import { Badge } from '@/components/ui/badge';
import { useUnreadOrdersCount } from '@/hooks/useUnreadOrdersCount';

const sellerMenuItems = [
  {
    group: 'Overview',
    items: [
      { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, end: true },
      { title: 'Analytics', url: '/seller/analytics', icon: BarChart3 },
      { title: 'Bundle Analytics', url: '/seller/analytics/bundles', icon: Layers },
    ]
  },
  {
    group: 'Products & Orders',
    items: [
      { title: 'Products', url: '/dashboard/products', icon: Package },
      { title: 'Bundles', url: '/seller/bundles', icon: Layers },
      { title: 'Collaboration Bundles', url: '/seller/collaboration-bundles', icon: Users },
      { title: 'Create Bundle', url: '/seller/collaboration-bundles/wizard', icon: Wand2 },
      { title: 'Bundle Invites', url: '/seller/collaboration-bundles/invitations', icon: Mail },
      { title: 'Inventory', url: '/seller/inventory', icon: PackageCheck },
      { title: 'Orders', url: '/dashboard/orders', icon: ShoppingCart },
      { title: 'Delivery', url: '/seller/delivery', icon: Truck },
      { title: 'Returns', url: '/dashboard/returns', icon: PackageX },
    ]
  },
  {
    group: 'Customers',
    items: [
      { title: 'CRM', url: '/dashboard/crm', icon: Users },
      { title: 'Leads', url: '/dashboard/leads', icon: UserPlus },
      { title: 'Chats', url: '/seller/chats', icon: MessageCircle },
      { title: 'Tickets', url: '/seller/tickets', icon: Ticket },
    ]
  },
  {
    group: 'Marketing & Finance',
    items: [
      { title: 'Shop Stories', url: '/dashboard/shop/stories', icon: FileText },
      { title: 'Promotions', url: '/seller/promos', icon: Tag },
      { title: 'Wallet', url: '/seller/wallet', icon: Wallet },
    ]
  },
  {
    group: 'Settings',
    items: [
      { title: 'Managers', url: '/dashboard/managers', icon: UserCog },
      { title: 'Shop Settings', url: '/dashboard/settings', icon: Settings },
    ]
  }
];

export function SellerSidebar() {
  const { state, isMobile } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const { unreadCount } = useUnreadOrdersCount();

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="border-b border-border p-3 sm:p-4">
        {(!isCollapsed || isMobile) && (
          <div className="flex items-center gap-2">
            <Store className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            <span className="font-semibold text-foreground text-sm sm:text-base">Seller Panel</span>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="overflow-y-auto">
        {sellerMenuItems.map((group) => (
          <SidebarGroup key={group.group}>
            {(!isCollapsed || isMobile) && (
              <SidebarGroupLabel className="text-xs">{group.group}</SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const showBadge = item.url === '/dashboard/orders' && unreadCount > 0;
                  
                  return (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton asChild>
                        <NavLink 
                          to={item.url} 
                          end={item.end}
                          className="flex items-center gap-2 sm:gap-3 hover:bg-accent/50 text-sm"
                          activeClassName="bg-accent text-accent-foreground font-medium"
                        >
                          <item.icon className="h-4 w-4 flex-shrink-0" />
                          {(!isCollapsed || isMobile) && (
                            <span className="truncate flex-1">{item.title}</span>
                          )}
                          {showBadge && (!isCollapsed || isMobile) && (
                            <Badge 
                              variant="destructive" 
                              className="ml-auto h-5 min-w-[20px] px-1.5 text-xs font-semibold"
                            >
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </Badge>
                          )}
                          {showBadge && isCollapsed && !isMobile && (
                            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" />
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}
